import { Close } from "@mui/icons-material";
import { useTranslation } from "@onefront/react-sdk";
import { Field } from "@onefront/react-workspace-components";
import { AtecoCodeLookup, ExtendedDatePicker, ExtendedRadioGroup, Infobox } from "@onefront/workspace-forms";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    FormControl,
    FormHelperText,
    IconButton,
    Radio,
    Skeleton,
    Stack,
    Toggle,
    useTheme
} from "@vapor/react-material";
import React, { useCallback, useEffect, useState } from "react";
import { BusinessFooterForm } from "../footer";
import WorkspaceDetailsSectionTitle from "../../../general/section-title";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import { Classifier } from "../../../../@types/enums/general/Classifier";
import { FF_FUNERAL_ATECO, FF_HEALTHCARD } from "../../../../utils/feature-flags";

export const BusinessDetailsForm = ({
    bodyRequest,
    handleChange,
    handleDataChange,
    onSave,
    onCancel,
    onEditPrevalent,
    isEditing,
    prevalentBusiness,
    usedAtecos,
    isNewBusiness,
    loading,
    requestType,
    workspaceData
}) => {
    const { t } = useTranslation();

    const {
        palette: {
            primary: { textSubduedColor: Cadet, successed: LaSalleGreen }
        }
    } = useTheme();

    const [displayErrors, setDisplayErrors] = useState(false);
    const [errors, setErrors] = useState({
        description: true,
        type: true,
        atecoCode: true,
        startDate: true,
        selfEmployed: true
    });
    const [openPrevalentDialog, setOpenPrevalentDialog] = useState(false);

    const handleDialog = () => {
        setOpenPrevalentDialog(!openPrevalentDialog);
    };

    const validate = useCallback(body => {
        const newErrors = {
            description: false,
            type: false,
            atecoCode: false,
            startDate: false,
            selfEmployed: false
        };

        Object.keys(newErrors).forEach(key => {
            if (key !== "selfEmployed" && !body?.[key]) {
                newErrors[key] = true;
            }
            if (key === "selfEmployed" && (body?.[key] === null || body?.[key] === undefined)) {
                newErrors[key] = true;
            }
        });

        setErrors(newErrors);
    }, []);

    useEffect(() => {
        validate(bodyRequest);
    }, [bodyRequest, validate]);

    return (
        <Stack>
            <Dialog
                open={openPrevalentDialog}
                onClose={handleDialog}
                aria-labelledby="alert-dialog-prevalentChoiseLabel"
                aria-describedby="alert-dialog-prevalentChoiseDescription"
                sx={{
                    "& .MuiDialog-paper": {
                        maxWidth: "500px"
                    }
                }}
            >
                <DialogTitle id="alert-dialog-prevalentChoiseLabel">
                    {t("business.dialog.prevalent.title")}
                    <IconButton color="primary" onClick={handleDialog}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <Divider variant="alert" />
                <DialogContent>
                    <DialogContentText id="alert-dialog-prevalentChoiseDescription">
                        {t("business.dialog.prevalent.description", {
                            businessName: prevalentBusiness?.description
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleDialog}>
                        {t("general.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => {
                            onEditPrevalent().then(() => {
                                handleDialog();
                            });
                        }}
                        disabled={requestType === "EDIT_PREVALENT" && loading}
                    >
                        {requestType === "EDIT_PREVALENT" && loading && (
                            <CircularProgress size={20} style={{ marginRight: 8 }} color="inherit" />
                        )}
                        {t("general.yes_substitute")}
                    </Button>
                </DialogActions>
            </Dialog>
            <Stack gap={isEditing ? 4 : 2} sx={{ width: "740px" }} mb={15}>
                <Field
                    component="textfield"
                    label={t("business.fields.description")}
                    labelTip={isEditing && t("business.tips.description")}
                    isEditing={isEditing}
                    loading={requestType === "MAIN" && loading}
                    fullWidth
                    onChange={val => {
                        handleChange(val, "description");
                    }}
                    value={bodyRequest?.description}
                    additionalProps={{
                        autoComplete: "off",
                        error: displayErrors && errors.description,
                        helperText: displayErrors && errors.description && t("general.fields.error.required"),
                        InputProps: {
                            autoComplete: "off"
                        },
                        required: true
                    }}
                />
                <Box>
                    <Field
                        component="radio"
                        label={t("business.fields.type.label")}
                        isEditing={isEditing}
                        radioGroupAlign="vertical"
                        loading={requestType === "MAIN" && loading}
                        options={[
                            {
                                id: "MAIN",
                                value: t("business.fields.type.MAIN")
                            },
                            {
                                id: "SECONDARY",
                                value: t("business.fields.type.SECONDARY")
                            }
                        ]}
                        onChange={val => {
                            prevalentBusiness?.id && val === "MAIN" ? handleDialog() : handleChange(val, "type");
                        }}
                        value={bodyRequest?.type}
                    />
                    {displayErrors && errors.type && (
                        <FormHelperText error={displayErrors && errors.type}>
                            {t("general.fields.error.required")}
                        </FormHelperText>
                    )}
                </Box>
                {workspaceData?.classifier === Classifier.COMPANY && (
                    <Box>
                        <ExtendedRadioGroup
                            name="selfEmployed"
                            label={"L’attività fa riferimento ad un professionista?"}
                            onChange={handleDataChange}
                            value={bodyRequest?.selfEmployed?.toString()}
                            editing={isEditing}
                            loading={loading}
                            sizeLoading="medium"
                        >
                            <FormControlLabel value={"true"} control={<Radio />} label={t("general.yes")} />

                            <FormControlLabel value={"false"} control={<Radio />} label={t("general.no")} />
                        </ExtendedRadioGroup>
                    </Box>
                )}

                <Box>
                    {loading && requestType === "MAIN" ? (
                        <Stack spacing={2}>
                            <Skeleton width="240px" />
                            <Skeleton width="240px" />
                        </Stack>
                    ) : (
                        <Box py={2}>
                            <AtecoCodeLookup
                                usedCodes={usedAtecos}
                                editing={isEditing}
                                loading={requestType === "MAIN" && loading}
                                selected={bodyRequest?.atecoCode}
                                onSelect={val => {
                                    handleChange(val?.code, "atecoCode");
                                    handleChange(val?.description, "atecoCodeDescription");
                                }}
                                onClear={() => {
                                    handleChange("", "atecoCode");
                                }}
                                error={displayErrors && errors.atecoCode}
                                onlyFullCode
                            />
                        </Box>
                    )}
                </Box>
                {FF_FUNERAL_ATECO && (requestType !== "MAIN" || !loading) && bodyRequest?.atecoCode === "96.03.00" && (
                    <Box>
                        {isEditing && (
                            <Infobox
                                title="Info sull'attività"
                                description="E' stato selezionato un codice ATECO che prevede l'obbligo di comunicazione, all'Agenzia delle Entrate, 
                                dei documenti attivi relativi a servizi di pompe funebri e attività connesse erogati a soggetti privati al fine di 
                                consentire l'elaborazione della dichiarazione precompilata."
                            />
                        )}
                        <Field
                            component="textfield"
                            label={!isEditing ? "Abilita comunicazione" : ""}
                            value={
                                <Stack direction={"row"} display={"flex"} alignItems={"center"}>
                                    Comunicazione delle spese funebri abilitata{" "}
                                    <FontAwesomeIcon
                                        style={{ marginLeft: 8, fontSize: 18 }}
                                        icon={faCheckCircle}
                                        color={LaSalleGreen}
                                    />
                                </Stack>
                            }
                        />
                    </Box>
                )}
                <Box>
                    <ExtendedDatePicker
                        name="startDate"
                        label={t("business.fields.startDate")}
                        value={bodyRequest?.startDate}
                        editing={isEditing}
                        onChange={(key: string, date: string | null) => {
                            handleChange(date, key);
                        }}
                        loading={loading}
                        required
                        displayError={displayErrors && errors.startDate}
                        helperText={displayErrors && errors.startDate ? t("general.fields.error.required") : null}
                        maxDate={new Date().toISOString()}
                        sx={{ width: "220px" }}
                    />
                </Box>
                {FF_HEALTHCARD && (
                    <>
                        <Stack direction="row" justifyContent="space-between" alignItems={"center"} width={768}>
                            <WorkspaceDetailsSectionTitle title={"Sistema Tessera Sanitaria"} />
                        </Stack>
                        {(requestType !== "MAIN" || !loading) && isEditing && (
                            <Box>
                                <Infobox
                                    title="Abilita invio"
                                    description="I dati dei documenti attivi relativi a prestazioni mediche erogate a soggetti privati, saranno predisposti per 
essere trasmessi al Sistema Tessera Sanitaria al fine di consentire all'Agenzia delle entrate di elaborare la 
dichiarazione precompilata."
                                />
                                <FormControl>
                                    <FormControlLabel
                                        label="abilita invio delle spese mediche al Sistema Tessera Sanitaria"
                                        control={
                                            <Toggle
                                                checked={bodyRequest?.healthCard}
                                                onChange={(e: React.BaseSyntheticEvent) => {
                                                    handleChange(e?.target?.checked, "healthCard");
                                                }}
                                            />
                                        }
                                    />
                                </FormControl>
                            </Box>
                        )}
                        {FF_HEALTHCARD && (requestType !== "MAIN" || !loading) && !isEditing && (
                            <Box>
                                <Field
                                    component="textfield"
                                    label="Abilita invio"
                                    value={
                                        bodyRequest?.healthCard ? (
                                            <Stack direction={"row"} display={"flex"} alignItems={"center"}>
                                                Invio delle spese mediche al Sistema Tessera Sanitaria abilitato{" "}
                                                <FontAwesomeIcon
                                                    style={{ marginLeft: 8, fontSize: 18 }}
                                                    icon={faCheckCircle}
                                                    color={LaSalleGreen}
                                                />
                                            </Stack>
                                        ) : (
                                            <Stack direction={"row"} display={"flex"} alignItems={"center"}>
                                                Invio delle spese mediche al Sistema Tessera Sanitaria non abilitato{" "}
                                                <FontAwesomeIcon
                                                    style={{ marginLeft: 8, fontSize: 18 }}
                                                    icon={faCircleXmark}
                                                    color={Cadet}
                                                />
                                            </Stack>
                                        )
                                    }
                                />
                            </Box>
                        )}
                    </>
                )}
                {isEditing && (
                    <BusinessFooterForm
                        isNewBusiness={isNewBusiness}
                        onBack={onCancel}
                        onForward={() => {
                            if (Object.keys(errors).some(key => errors[key])) {
                                setDisplayErrors(true);
                            } else {
                                setDisplayErrors(false);
                                onSave();
                            }
                        }}
                        loading={requestType === "CREATE_OR_UPDATE" && loading}
                        disabledForward={requestType === "CREATE_OR_UPDATE" && loading}
                    />
                )}
            </Stack>
        </Stack>
    );
};
